import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Trash2, User, Globe, GraduationCap, MapPin, CheckCircle2 } from 'lucide-react';
import api, { BACKEND_URL } from '../services/api';
import OfficialApplicationPrint from '../components/OfficialApplicationPrint';
import DocumentViewer from '../components/DocumentViewer';
const ApplicationProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [appInfo, setAppInfo] = useState(null);
  const [studentInfo, setStudentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Document Management State
  const [documents, setDocuments] = useState([]);
  const [docCategory, setDocCategory] = useState('Passport');
  const [docFile, setDocFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [pendingStatus, setPendingStatus] = useState('');

  const categories = [
    'Passport', 'Citizenship', 
    "Father's Citizenship", "Mother's Citizenship",
    'SEE Transcript', 'SEE Character Certificate',
    '+2 Transcript', '+2 Character Certificate',
    'Bachelor Transcript', 'Bachelor Character Certificate'
  ];

  const wardCategories = [
    'Relationship Certificate', 'Annual Income Certificate', 'Occupation Certificate',
    'Birth Certificate', 'Address Verification Certificate', 'Tax Clearance Certificate', 'Other'
  ];
  const [wardDocCategory, setWardDocCategory] = useState(wardCategories[0]);
  const [wardDocFile, setWardDocFile] = useState(null);
  const [wardUploading, setWardUploading] = useState(false);
  const [wardError, setWardError] = useState('');
  const [wardSuccess, setWardSuccess] = useState('');
  const [customWardCategory, setCustomWardCategory] = useState('');


  const [additionalDocFile, setAdditionalDocFile] = useState(null);
  const [additionalUploading, setAdditionalUploading] = useState(false);
  const [additionalError, setAdditionalError] = useState('');
  const [additionalSuccess, setAdditionalSuccess] = useState('');
  const [customAdditionalCategory, setCustomAdditionalCategory] = useState('');

  const MAX_FILE_MB = 5;
  const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

  const validateFile = (file, setErrFn) => {
    if (file && file.size > MAX_FILE_BYTES) {
      setErrFn(`File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed size is ${MAX_FILE_MB} MB.`);
      return false;
    }
    return true;
  };

  const [photoUploading, setPhotoUploading] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [downloadingDocsZip, setDownloadingDocsZip] = useState(false);

  const getFileSrc = (filePath) => {
    if (!filePath) return null;
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    return `${BACKEND_URL}${filePath}`;
  };

  const fetchDocuments = useCallback(async (studentId) => {
    try {
      const res = await api.get(`/documents/student/${studentId}`);
      setDocuments(res.data);
    } catch (err) {
      console.error("Error fetching documents", err);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const appRes = await api.get(`/applications/${id}`);
      const appData = appRes.data;
      setAppInfo(appData);

      if (appData.student_id) {
          const studentRes = await api.get(`/students/${appData.student_id}`);
          setStudentInfo(studentRes.data);
          fetchDocuments(appData.student_id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, fetchDocuments]);

  useEffect(() => {
    fetchProfile();
  }, [id, fetchProfile]);

  const handlePrintOfficialForm = () => {
    window.print();
  };

  const handleDeleteApplication = async () => {
      if (!window.confirm(`Are you sure you want to permanently delete this application? This action cannot be undone.`)) return;
      
      try {
          await api.delete(`/applications/${id}`);
          alert('Application deleted successfully.');
          navigate('/applications');
      } catch (err) {
          alert('Error deleting application. Only admins or creators can delete.');
      }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!docFile) { setError('Please select a document file first.'); return; }
    if (!validateFile(docFile, setError)) return;
    
    try {
      setUploading(true);
      setError('');
      const formData = new FormData();
      formData.append('file', docFile);
      formData.append('student_id', appInfo.student_id);
      formData.append('category', docCategory);

      await api.post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSuccess('Document uploaded successfully!');
      setDocFile(null);
      fetchDocuments(appInfo.student_id);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error("Upload error:", err.response?.data);
      setError('Failed to upload document. ' + (err.response?.data?.error || ''));
    } finally {
      setUploading(false);
    }
  };

  const handleWardDocumentUpload = async (e) => {
    e.preventDefault();
    if (!wardDocFile) { setWardError('Please select a document file first.'); return; }
    if (!validateFile(wardDocFile, setWardError)) return;
    
    try {
      setWardUploading(true);
      setWardError('');
      const formData = new FormData();
      formData.append('file', wardDocFile);
      formData.append('student_id', appInfo.student_id);
      const categoryToUpload = wardDocCategory === 'Other' && customWardCategory.trim() !== '' 
        ? '(Ward) ' + customWardCategory.trim() 
        : wardDocCategory;
      formData.append('category', categoryToUpload);

      await api.post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setWardSuccess('Document uploaded successfully!');
      setWardDocFile(null);
      setCustomWardCategory('');
      fetchDocuments(appInfo.student_id);
      setTimeout(() => setWardSuccess(''), 3000);
    } catch (err) {
      console.error("Upload error:", err.response?.data);
      setWardError('Failed to upload document. ' + (err.response?.data?.error || ''));
    } finally {
      setWardUploading(false);
    }
  };

  const handleAdditionalDocumentUpload = async (e) => {
    e.preventDefault();
    if (!additionalDocFile) { setAdditionalError('Please select a document file first.'); return; }
    if (!validateFile(additionalDocFile, setAdditionalError)) return;
    
    try {
      setAdditionalUploading(true);
      setAdditionalError('');
      const formData = new FormData();
      formData.append('file', additionalDocFile);
      formData.append('student_id', appInfo.student_id);
      
      if (!customAdditionalCategory.trim()) {
        setAdditionalError('Please enter a document name.');
        setAdditionalUploading(false);
        return;
      }
      formData.append('category', customAdditionalCategory.trim());

      await api.post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAdditionalSuccess('Document uploaded successfully!');
      setAdditionalDocFile(null);
      setCustomAdditionalCategory('');
      fetchDocuments(appInfo.student_id);
      setTimeout(() => setAdditionalSuccess(''), 3000);
    } catch (err) {
      console.error("Upload error:", err.response?.data);
      setAdditionalError('Failed to upload document. ' + (err.response?.data?.error || ''));
    } finally {
      setAdditionalUploading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateFile(file, (msg) => alert(msg))) return;

    try {
      setPhotoUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('student_id', appInfo.student_id);
      formData.append('category', 'Student Photo');

      await api.post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      fetchDocuments(appInfo.student_id);
    } catch (err) {
      console.error("Photo upload error:", err);
      alert('Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDownloadDocsOnlyZip = async () => {
    try {
      setDownloadingDocsZip(true);
      const response = await api.get(`/documents/student/${studentInfo.id}/zip-documents`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${studentInfo.full_name.replace(/[^a-z0-9]/gi, '_')}_Documents_Only.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Documents ZIP download error:", err);
      alert("Failed to download documents ZIP file.");
    } finally {
      setDownloadingDocsZip(false);
    }
  };

  const handleDownloadSingleDocument = async (docPath, suggestedName, e) => {
    e.preventDefault();
    try {
      const isCloudUrl = docPath.startsWith('http://') || docPath.startsWith('https://');
      const fetchUrl = isCloudUrl ? docPath : `${BACKEND_URL}${docPath}`;
      
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      let ext = 'pdf';
      try {
        const urlObj = new URL(docPath);
        ext = urlObj.pathname.split('.').pop() || 'pdf';
      } catch (err) {
        ext = docPath.split('.').pop() || 'pdf';
      }
      
      const filenameWithExt = suggestedName.includes('.') ? suggestedName : `${suggestedName}.${ext}`;
      link.setAttribute('download', filenameWithExt);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Single document download error:", err);
      const finalUrl = docPath.startsWith('http') ? docPath : `${BACKEND_URL}${docPath}`;
      window.open(finalUrl, '_blank');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/documents/${docId}`);
      fetchDocuments(appInfo.student_id);
    } catch (err) {
      setError('Failed to delete document.');
    }
  };

  const handleStatusChange = async (newStatus) => {
      if (newStatus === 'Rejected') {
          setPendingStatus(newStatus);
          setShowRejectionModal(true);
          return;
      }

      if (!window.confirm(`Are you sure you want to change the status to ${newStatus}?`)) return;
      try {
          setStatusUpdating(true);
          await api.put(`/applications/${id}`, { 
              ...appInfo, 
              status: newStatus,
              rejection_reason: null
          });
          setAppInfo(prev => ({ ...prev, status: newStatus, rejection_reason: null }));
          setSuccess('Application status updated successfully!');
          setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
          console.error("Error updating status:", err);
          const serverError = err.response?.data?.error || err.message;
          alert('Failed to update status: ' + serverError);
      } finally {
          setStatusUpdating(false);
      }
  };

  const submitRejection = async () => {
      if (!rejectionReason.trim()) {
          alert('Please provide a reason for rejection.');
          return;
      }
      try {
          setStatusUpdating(true);
          await api.put(`/applications/${id}`, { 
              ...appInfo, 
              status: pendingStatus,
              rejection_reason: rejectionReason
          });
          setAppInfo(prev => ({ ...prev, status: pendingStatus, rejection_reason: rejectionReason }));
          setSuccess('Application rejected with reason.');
          setShowRejectionModal(false);
          setRejectionReason('');
          setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
          console.error("Error rejecting:", err);
          const serverError = err.response?.data?.error || err.message;
          alert('Failed to reject application: ' + serverError);
      } finally {
          setStatusUpdating(false);
      }
  };

  const openViewer = (url, title) => setViewerDoc({ url, title });

  if (loading) return <div className="p-8 text-center text-gray-500">Loading Application Profile...</div>;
  if (!appInfo || !studentInfo) return <div className="p-8 text-center text-red-500">Application not found.</div>;

  const details = appInfo.additional_details || {};

  const photoDoc = documents.find(doc => doc.category === 'Student Photo');
  const photoSrc = photoDoc ? getFileSrc(photoDoc.file_path) : (studentInfo.photo_url ? getFileSrc(studentInfo.photo_url) : details.photo_base64);

  const academicDocs = documents.filter(doc => categories.includes(doc.category));
  const wardDocs = documents.filter(doc => wardCategories.includes(doc.category) || doc.category.startsWith('(Ward) '));
  const additionalDocs = documents.filter(doc => 
    !categories.includes(doc.category) && 
    !wardCategories.includes(doc.category) && 
    !doc.category.startsWith('(Ward) ') && 
    doc.category !== 'Student Photo'
  );

  return (
    <div className="relative">
      {viewerDoc && <DocumentViewer url={viewerDoc.url} title={viewerDoc.title} onClose={() => setViewerDoc(null)} />}
      <style dangerouslySetInnerHTML={{ __html: `
          @media print {
              .no-print { display: none !important; }
              body { 
                margin: 0 !important; 
                padding: 0 !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                background: white !important; 
                font-family: "Times New Roman", Times, serif !important; 
              }
              .main-container { padding: 0 !important; margin: 0 !important; }
              html, body { height: auto !important; overflow: visible !important; }
          }
      `}} />

      <div className="mb-6 flex justify-between items-center no-print">
          <Link className="text-sm text-blue-600 flex items-center gap-1 hover:underline" to="/applications">&larr; Back to Directory</Link>
          <div className="flex gap-3">
              {documents.length > 0 && (
                <>
                  <button 
                      onClick={handleDownloadDocsOnlyZip}
                      disabled={downloadingDocsZip}
                      className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm font-semibold text-blue-700 hover:bg-blue-600 hover:text-white flex items-center gap-2 shadow-sm transition disabled:opacity-50"
                      title="Download Documents Only as ZIP"
                  >
                      <span>🗜️</span> {downloadingDocsZip ? 'Zipping Docs...' : 'Export Documents (ZIP)'}
                  </button>
                </>
              )}
              <button 
                  onClick={handlePrintOfficialForm}
                  className="px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-sm font-semibold text-indigo-700 hover:bg-indigo-100 flex items-center gap-2 shadow-sm transition"
              >
                  <span>📝</span> Print Application Form
              </button>
              {user?.role === 'Admin' && (
                  <button 
                      onClick={handleDeleteApplication}
                      className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm hover:bg-rose-700 transition"
                  >
                      <Trash2 size={16} />
                      Delete Application
                  </button>
              )}
          </div>
      </div>

      {/* OFFICIAL PRINT VIEW (High Fidelity Replica) */}
      <div className="hidden print:block official-view">
          <OfficialApplicationPrint 
              app={appInfo} 
              student={studentInfo}
              printedBy={user?.full_name || user?.username || 'Admin'}
          />
      </div>

      {/* DASHBOARD VIEW (Normal Web View) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in duration-500 no-print profile-view">
        
        {/* Left Col: Info */}
        <div className="lg:col-span-1 space-y-6">
            <div className="card text-center overflow-hidden border-b-4 border-b-blue-600 shadow-sm">
                <div className="w-32 h-32 mx-auto mb-4">
                    {photoSrc ? (
                        <img src={photoSrc} alt="Profile" className="w-full h-full rounded-full object-cover shadow-md border-4 border-white" />
                    ) : (
                        <div className="w-full h-full rounded-full bg-blue-50 text-blue-400 flex items-center justify-center border-4 border-white shadow-sm">
                            <User size={64} />
                        </div>
                    )}
                </div>

                <div className="flex flex-col items-center gap-2 mb-4">
                    <label className="cursor-pointer px-4 py-1.5 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg text-xs font-black hover:bg-blue-600 hover:text-white transition-all shadow-sm w-full max-w-[200px]">
                        {photoUploading ? '⏳ Uploading...' : '📷 Upload New Photo'}
                        <input type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} disabled={photoUploading} />
                    </label>

                    {photoSrc && (
                        <div className="flex gap-2 w-full max-w-[200px]">
                            <button 
                                onClick={(e) => {
                                    if (photoDoc) {
                                        handleDownloadSingleDocument(photoDoc.file_path, `Photo_${studentInfo.full_name}`, e);
                                    } else if (studentInfo.photo_url) {
                                        handleDownloadSingleDocument(studentInfo.photo_url, `Photo_${studentInfo.full_name}`, e);
                                    } else if (details.photo_base64) {
                                        e.preventDefault();
                                        const link = document.createElement('a');
                                        link.href = details.photo_base64;
                                        link.setAttribute('download', `Photo_${studentInfo.full_name}.jpg`);
                                        document.body.appendChild(link);
                                        link.click();
                                        link.parentNode.removeChild(link);
                                    }
                                }}
                                className="flex-1 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-[10px] font-black hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex items-center justify-center gap-1 border border-emerald-200"
                            >
                                <span>📥</span> Download
                            </button>
                            {user?.role === 'Admin' && photoDoc && (
                                <button 
                                    onClick={() => handleDeleteDocument(photoDoc.id)}
                                    className="px-3 py-1.5 bg-rose-50 text-rose-600 rounded-lg text-[10px] font-black hover:bg-rose-600 hover:text-white transition-all shadow-sm flex items-center justify-center border border-rose-200"
                                    title="Delete Photo"
                                >
                                    🗑️
                                </button>
                            )}
                        </div>
                    )}
                </div>
                
                <h2 className="text-xl font-bold text-gray-800">{studentInfo.full_name}</h2>
                <span className={`inline-block mt-2 px-3 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800`}>
                    {studentInfo.visa_type} Visa
                </span>
                
                <div className="mt-6 text-left border-t border-gray-100 pt-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Passport No.</p>
                        <p className="font-bold text-gray-800 bg-gray-100 px-2 py-0.5 rounded text-sm">{studentInfo.passport_no || studentInfo.passport_number}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Email</p>
                        <p className="font-medium text-gray-800 truncate">{studentInfo.email}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Phone</p>
                        <p className="font-medium text-gray-800">{studentInfo.phone}</p>
                    </div>
                </div>
            </div>
        </div>

        {/* Right Col: Details */}
        <div className="lg:col-span-2 space-y-6">
            <div className="card border-b-4 border-b-indigo-500 shadow-sm">
                <h3 className="font-bold text-gray-800 border-b pb-3 mb-4 flex items-center gap-2 text-lg">
                    <span className="text-blue-500">📋</span> Application Processing Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1 flex items-center gap-1"><Globe size={14}/> Destination Country</p>
                        <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{appInfo.country}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1 flex items-center gap-1"><GraduationCap size={14}/> University / Institution</p>
                        <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{appInfo.university}</p>
                    </div>
                    {user?.role === 'Admin' && appInfo.status && (
                        <div>
                            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1 flex items-center gap-1"><CheckCircle2 size={14}/> Current Status</p>
                            <div className="flex items-center gap-2 border-b border-gray-50 pb-2">
                                <select 
                                    value={appInfo.status}
                                    onChange={(e) => handleStatusChange(e.target.value)}
                                    disabled={statusUpdating}
                                    className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest outline-none border-none cursor-pointer transition-all hover:ring-2 hover:ring-offset-1 ${
                                        appInfo.status === 'Approved' ? 'bg-emerald-100 text-emerald-700 hover:ring-emerald-300' :
                                        appInfo.status === 'Rejected' ? 'bg-rose-100 text-rose-700 hover:ring-rose-300' :
                                        appInfo.status === 'Selected' ? 'bg-indigo-100 text-indigo-700 hover:ring-indigo-300' :
                                        'bg-amber-100 text-amber-700 hover:ring-amber-300'
                                    }`}
                                >
                                    <option value="Applied">Applied</option>
                                    <option value="Selected">Selected</option>
                                    <option value="Processing">Processing</option>
                                    <option value="Approved">Approved</option>
                                    <option value="Rejected">Rejected</option>
                                </select>
                                {statusUpdating && <span className="animate-spin text-[10px]">⏳</span>}
                            </div>
                            {appInfo.status === 'Rejected' && appInfo.rejection_reason && (
                                <div className="mt-2 p-2 bg-rose-50 border border-rose-100 rounded-lg">
                                    <p className="text-[10px] font-black text-rose-800 uppercase tracking-widest mb-1">Rejection Reason:</p>
                                    <p className="text-xs text-rose-600 font-medium italic">"{appInfo.rejection_reason}"</p>
                                </div>
                            )}
                        </div>
                    )}
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1 flex items-center gap-1"><MapPin size={14}/> Process Target</p>
                        <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{appInfo.application_name}</p>
                    </div>
                </div>
            </div>



            <div className="card border-b-4 border-b-blue-500 shadow-sm mt-6">
                <h3 className="font-bold text-gray-800 border-b pb-3 mb-4 flex items-center gap-2 text-lg">
                    <span className="text-blue-500">📁</span> Academic Documents
                </h3>

                {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-lg text-xs font-bold animate-in zoom-in-95">{success}</div>}
                {error && <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-700 rounded-lg text-xs font-bold animate-in zoom-in-95">{error}</div>}

                {/* Upload Form */}
                <form onSubmit={handleDocumentUpload} className="bg-gray-50 rounded-xl p-4 border border-gray-100 shadow-sm mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                        <div>
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-tighter mb-1.5 ml-1">Category</label>
                            <select 
                                className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:border-blue-500"
                                value={docCategory}
                                onChange={(e) => setDocCategory(e.target.value)}
                            >
                                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 flex flex-col gap-1">
                              <label className="bg-white border border-dashed border-gray-300 rounded-lg px-3 py-2 text-xs font-medium text-gray-400 cursor-pointer overflow-hidden whitespace-nowrap block">
                                <input type="file" className="hidden" onChange={(e) => { const f = e.target.files[0]; if (f && f.size > MAX_FILE_BYTES) { setError(`File too large (${(f.size/(1024*1024)).toFixed(1)} MB). Max ${MAX_FILE_MB} MB.`); e.target.value=''; setDocFile(null); } else { setError(''); setDocFile(f); } }} />
                                {docFile ? docFile.name : 'Choose File...'}
                              </label>
                              <p className="text-[10px] text-gray-400 ml-1">JPG, PNG, PDF, Word · Max {MAX_FILE_MB} MB</p>
                            </div>
                            <button 
                                type="submit" 
                                disabled={!docFile || uploading}
                                className="h-9 px-4 bg-gray-900 text-white rounded-lg font-black text-xs hover:bg-blue-600 active:scale-95 disabled:opacity-30 transition-all shrink-0"
                            >
                                {uploading ? '⏳' : 'UPLOAD'}
                            </button>
                        </div>
                    </div>
                </form>

                {/* List */}
                <div className="space-y-3">
                    {academicDocs.length === 0 ? (
                        <div className="py-8 border-2 border-dashed border-gray-100 rounded-xl text-center">
                            <p className="text-xs font-bold text-gray-400 italic">No academic documents uploaded yet.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {academicDocs.map(doc => (
                                <div key={doc.id} className="group bg-white border border-gray-100 rounded-xl p-3 flex items-center justify-between hover:border-blue-200 hover:shadow-sm transition-all animate-in slide-in-from-bottom-2">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-lg grayscale group-hover:grayscale-0 transition-all shrink-0">📄</div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-black text-gray-800 uppercase tracking-tight truncate">{doc.category}</p>
                                            <p className="text-[9px] text-gray-400 font-bold">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button 
                                            type="button"
                                            onClick={() => openViewer(getFileSrc(doc.file_path), doc.category)}
                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                            title="View Document"
                                        >
                                            👁️
                                        </button>
                                        <button 
                                            onClick={(e) => handleDownloadSingleDocument(doc.file_path, doc.category + "_" + studentInfo.full_name, e)}
                                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                                            title="Download Document"
                                        >
                                            📥
                                        </button>
                                        {user?.role === 'Admin' && (
                                            <button 
                                                onClick={() => handleDeleteDocument(doc.id)}
                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                title="Delete"
                                            >
                                                🗑️
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="card border-b-4 border-b-amber-500 shadow-sm mt-6">
                <h3 className="font-bold text-gray-800 border-b pb-3 mb-4 flex items-center justify-between text-lg">
                    <div className="flex items-center gap-2">
                        <span className="text-amber-500">🏛️</span> Ward Documents
                    </div>
                </h3>

                {wardSuccess && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-lg text-xs font-bold animate-in zoom-in-95">{wardSuccess}</div>}
                {wardError && <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-700 rounded-lg text-xs font-bold animate-in zoom-in-95">{wardError}</div>}

                {/* Upload Form */}
                <form onSubmit={handleWardDocumentUpload} className="bg-gray-50 rounded-xl p-4 border border-gray-100 shadow-sm mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                        <div>
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-tighter mb-1.5 ml-1">Category</label>
                            <div className="flex flex-col gap-2">
                                <select 
                                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:border-amber-500"
                                    value={wardDocCategory}
                                    onChange={(e) => setWardDocCategory(e.target.value)}
                                >
                                    {wardCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                                {wardDocCategory === 'Other' && (
                                    <input
                                        type="text"
                                        placeholder="Enter document name"
                                        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:border-amber-500"
                                        value={customWardCategory}
                                        onChange={(e) => setCustomWardCategory(e.target.value)}
                                        required
                                    />
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 flex flex-col gap-1">
                              <label className="bg-white border border-dashed border-gray-300 rounded-lg px-3 py-2 text-xs font-medium text-gray-400 cursor-pointer overflow-hidden whitespace-nowrap block">
                                <input type="file" className="hidden" onChange={(e) => { const f = e.target.files[0]; if (f && f.size > MAX_FILE_BYTES) { setWardError(`File too large (${(f.size/(1024*1024)).toFixed(1)} MB). Max ${MAX_FILE_MB} MB.`); e.target.value=''; setWardDocFile(null); } else { setWardError(''); setWardDocFile(f); } }} />
                                {wardDocFile ? wardDocFile.name : 'Choose File...'}
                              </label>
                              <p className="text-[10px] text-gray-400 ml-1">JPG, PNG, PDF, Word · Max {MAX_FILE_MB} MB</p>
                            </div>
                            <button 
                                type="submit" 
                                disabled={!wardDocFile || wardUploading}
                                className="h-9 px-4 bg-gray-900 text-white rounded-lg font-black text-xs hover:bg-amber-600 active:scale-95 disabled:opacity-30 transition-all shrink-0"
                            >
                                {wardUploading ? '⏳' : 'UPLOAD'}
                            </button>
                        </div>
                    </div>
                </form>

                {/* List */}
                <div className="space-y-3">
                    {wardDocs.length === 0 ? (
                        <div className="py-8 border-2 border-dashed border-gray-100 rounded-xl text-center">
                            <p className="text-xs font-bold text-gray-400 italic">No ward documents uploaded yet.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {wardDocs.map(doc => (
                                <div key={doc.id} className="group bg-white border border-gray-100 rounded-xl p-3 flex items-center justify-between hover:border-amber-200 hover:shadow-sm transition-all animate-in slide-in-from-bottom-2">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center text-lg grayscale group-hover:grayscale-0 transition-all shrink-0">📄</div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-black text-gray-800 uppercase tracking-tight truncate">{doc.category}</p>
                                            <p className="text-[9px] text-gray-400 font-bold">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button 
                                            type="button"
                                            onClick={() => openViewer(getFileSrc(doc.file_path), doc.category)}
                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                            title="View Document"
                                        >
                                            👁️
                                        </button>
                                        <button 
                                            onClick={(e) => handleDownloadSingleDocument(doc.file_path, doc.category.replace('(Ward) ', '') + "_" + studentInfo.full_name, e)}
                                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                                            title="Download Document"
                                        >
                                            📥
                                        </button>
                                        {user?.role === 'Admin' && (
                                            <button 
                                                onClick={() => handleDeleteDocument(doc.id)}
                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                title="Delete"
                                            >
                                                🗑️
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="card border-b-4 border-b-purple-500 shadow-sm mt-6">
                <h3 className="font-bold text-gray-800 border-b pb-3 mb-4 flex items-center justify-between text-lg">
                    <div className="flex items-center gap-2">
                        <span className="text-purple-500">➕</span> Additional Documents
                    </div>
                </h3>

                {additionalSuccess && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-lg text-xs font-bold animate-in zoom-in-95">{additionalSuccess}</div>}
                {additionalError && <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-700 rounded-lg text-xs font-bold animate-in zoom-in-95">{additionalError}</div>}

                {/* Upload Form */}
                <form onSubmit={handleAdditionalDocumentUpload} className="bg-gray-50 rounded-xl p-4 border border-gray-100 shadow-sm mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                        <div>
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-tighter mb-1.5 ml-1">Document Name</label>
                            <input
                                type="text"
                                placeholder="Enter document name..."
                                className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:border-purple-500"
                                value={customAdditionalCategory}
                                onChange={(e) => setCustomAdditionalCategory(e.target.value)}
                                required
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 flex flex-col gap-1">
                              <label className="bg-white border border-dashed border-gray-300 rounded-lg px-3 py-2 text-xs font-medium text-gray-400 cursor-pointer overflow-hidden whitespace-nowrap block">
                                <input type="file" className="hidden" onChange={(e) => { const f = e.target.files[0]; if (f && f.size > MAX_FILE_BYTES) { setAdditionalError(`File too large (${(f.size/(1024*1024)).toFixed(1)} MB). Max ${MAX_FILE_MB} MB.`); e.target.value=''; setAdditionalDocFile(null); } else { setAdditionalError(''); setAdditionalDocFile(f); } }} />
                                {additionalDocFile ? additionalDocFile.name : 'Choose File...'}
                              </label>
                              <p className="text-[10px] text-gray-400 ml-1">JPG, PNG, PDF, Word · Max {MAX_FILE_MB} MB</p>
                            </div>
                            <button 
                                type="submit" 
                                disabled={!additionalDocFile || additionalUploading}
                                className="h-9 px-4 bg-gray-900 text-white rounded-lg font-black text-xs hover:bg-purple-600 active:scale-95 disabled:opacity-30 transition-all shrink-0"
                            >
                                {additionalUploading ? '⏳' : 'UPLOAD'}
                            </button>
                        </div>
                    </div>
                </form>

                {/* List */}
                <div className="space-y-3">
                    {additionalDocs.length === 0 ? (
                        <div className="py-8 border-2 border-dashed border-gray-100 rounded-xl text-center">
                            <p className="text-xs font-bold text-gray-400 italic">No additional documents uploaded yet.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {additionalDocs.map(doc => (
                                <div key={doc.id} className="group bg-white border border-gray-100 rounded-xl p-3 flex items-center justify-between hover:border-purple-200 hover:shadow-sm transition-all animate-in slide-in-from-bottom-2">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center text-lg grayscale group-hover:grayscale-0 transition-all shrink-0">📄</div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-black text-gray-800 uppercase tracking-tight truncate">
                                                {doc.category.replace('(Ward) ', '')}
                                            </p>
                                            <p className="text-[9px] text-gray-400 font-bold">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button 
                                            type="button"
                                            onClick={() => openViewer(getFileSrc(doc.file_path), doc.category)}
                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                            title="View Document"
                                        >
                                            👁️
                                        </button>
                                        <button 
                                            onClick={(e) => handleDownloadSingleDocument(doc.file_path, doc.category.replace('(Ward) ', '') + "_" + studentInfo.full_name, e)}
                                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                                            title="Download Document"
                                        >
                                            📥
                                        </button>
                                        {user?.role === 'Admin' && (
                                            <button 
                                                onClick={() => handleDeleteDocument(doc.id)}
                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                title="Delete"
                                            >
                                                🗑️
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectionModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
              <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden border border-gray-100 animate-in zoom-in-95 duration-300">
                  <div className="p-6 bg-rose-600 text-white">
                      <h4 className="text-xl font-black flex items-center gap-2">
                          <span>🚫</span> Reject Application
                      </h4>
                      <p className="text-xs text-rose-100 font-bold uppercase tracking-widest mt-1 opacity-80">Please provide a reason for rejection</p>
                  </div>
                  <div className="p-6 space-y-4">
                      <textarea 
                          className="w-full h-32 p-4 bg-gray-50 border-2 border-gray-100 rounded-2xl text-sm font-medium focus:border-rose-500 focus:bg-white outline-none transition-all placeholder:text-gray-400"
                          placeholder="Type the reason for rejection here..."
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                      />
                      <div className="flex gap-3">
                          <button 
                              onClick={() => {
                                  setShowRejectionModal(false);
                                  setRejectionReason('');
                              }}
                              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all active:scale-95"
                          >
                              Cancel
                          </button>
                          <button 
                              onClick={submitRejection}
                              disabled={statusUpdating}
                              className="flex-1 py-3 bg-rose-600 text-white rounded-xl font-black shadow-lg shadow-rose-200 hover:bg-rose-700 transition-all active:scale-95 disabled:opacity-50"
                          >
                              {statusUpdating ? 'Processing...' : 'Confirm Rejection'}
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default ApplicationProfile;
